import { GameState, GameConfig, GAME_EVENTS, Piece as IPiece } from '@/types/game';
import { SHAPES, COLORS } from './constants';
import { Piece } from './Piece';

// TICK-BASED GAME ENGINE - Everything runs on ticks!
export class GameEngine extends EventTarget {
  private state: GameState;
  private config: GameConfig;
  private currentTick: number = 0;
  
  // Tick rates (in game ticks)
  private readonly FALL_RATE_BASE = 30; // Base: fall every 30 ticks
  private readonly CLEAR_ANIMATION_TICKS = 10;
  private readonly CASCADE_DELAY_TICKS = 5;
  
  constructor(config: GameConfig) {
    super();
    this.config = config;
    this.state = this.createInitialState();
  }

  private createInitialState(): GameState {
    return {
      board: Array(this.config.rows).fill(null).map(() => Array(this.config.cols).fill(null)),
      pieces: new Map(),
      selected: null,
      moves: 0,
      level: 1,
      animating: false,
      processingCascades: false,
      score: 0,
      combo: 0,
      corruptionLevel: 0,
      gameOver: false,
      timeRemaining: 7200, // 7200 ticks = 2 minutes at 60 ticks/sec
      lastMoveTime: 0,
      fallingPieces: new Set(),
      lastFallTime: 0,
      fallSpeed: this.FALL_RATE_BASE,
    };
  }

  getState(): Readonly<GameState> {
    return this.state;
  }

  // MAIN GAME TICK - Called 60 times per second
  public tick(): void {
    if (this.state.gameOver) return;
    
    this.currentTick++;
    
    // Update timer
    if (this.state.timeRemaining > 0) {
      this.state.timeRemaining--;
      if (this.state.timeRemaining === 0) {
        this.handleGameOver();
        return;
      }
      
      // Emit time update every second (60 ticks)
      if (this.currentTick % 60 === 0) {
        this.dispatchEvent(new CustomEvent(GAME_EVENTS.TIME_UPDATE, {
          detail: { 
            timeRemaining: Math.floor(this.state.timeRemaining / 60), // Convert to seconds
            corruptionLevel: this.state.corruptionLevel 
          }
        }));
      }
    }
    
    // Update corruption
    if (this.currentTick % 60 === 0) { // Every second
      this.state.corruptionLevel += 0.001 * this.state.level;
      if (this.state.corruptionLevel >= this.config.rows) {
        this.handleGameOver();
        return;
      }
    }
    
    // Update fall speed based on level
    this.state.fallSpeed = Math.max(5, this.FALL_RATE_BASE - (this.state.level - 1) * 3);
    
    // Handle falling pieces
    if (this.currentTick % this.state.fallSpeed === 0) {
      this.processFallingPieces();
    }
    
    // Check for line clears every tick (when not animating)
    if (!this.state.animating && this.currentTick % 5 === 0) {
      this.checkLineClears();
    }
  }
  
  private processFallingPieces(): void {
    if (this.state.fallingPieces.size === 0) {
      // No pieces falling, check if any should start falling
      this.identifyFallingPieces();
      return;
    }
    
    const piecesToLand = new Set<number>();
    const movedPieces = new Map<number, { from: number; to: number }>();
    
    // Move each falling piece down one row
    this.state.fallingPieces.forEach(pieceId => {
      const piece = this.state.pieces.get(pieceId);
      if (!piece) {
        this.state.fallingPieces.delete(pieceId);
        return;
      }
      
      if (this.canPieceFall(piece)) {
        // Clear old position
        piece.cells.forEach(cell => {
          this.state.board[cell.y][cell.x] = null;
        });
        
        // Move down
        piece.cells = piece.cells.map(cell => ({
          x: cell.x,
          y: cell.y + 1
        }));
        
        // Set new position
        piece.cells.forEach(cell => {
          this.state.board[cell.y][cell.x] = pieceId;
        });
        
        movedPieces.set(pieceId, { from: -1, to: 0 });
      } else {
        piecesToLand.add(pieceId);
      }
    });
    
    // Remove landed pieces from falling set
    piecesToLand.forEach(id => {
      this.state.fallingPieces.delete(id);
    });
    
    // Emit movement event
    if (movedPieces.size > 0) {
      this.dispatchEvent(new CustomEvent(GAME_EVENTS.GRAVITY_APPLIED, {
        detail: { fallingPieces: movedPieces }
      }));
    }
    
    // If pieces landed, mark for line clear check
    if (piecesToLand.size > 0) {
      this.state.lastFallTime = this.currentTick;
    }
  }
  
  private canPieceFall(piece: IPiece): boolean {
    return piece.cells.every(cell => {
      const newY = cell.y + 1;
      if (newY >= this.config.rows) return false;
      const occupant = this.state.board[newY][cell.x];
      return occupant === null || occupant === piece.id;
    });
  }
  
  private identifyFallingPieces(): void {
    this.state.pieces.forEach(piece => {
      if (this.canPieceFall(piece)) {
        this.state.fallingPieces.add(piece.id);
      }
    });
  }
  
  private checkLineClears(): void {
    // Don't check if we just cleared (cooldown)
    if (this.currentTick - this.state.lastFallTime < this.CASCADE_DELAY_TICKS) return;
    
    const paths = this.findLeftToRightPaths();
    const fullLines = this.findFullLines();
    
    if (paths.length > 0 || fullLines.length > 0) {
      this.state.animating = true;
      
      // Clear the lines
      this.clearPathsAndLines(paths, fullLines);
      
      // Schedule animation end
      setTimeout(() => {
        this.state.animating = false;
        this.identifyFallingPieces();
      }, this.CLEAR_ANIMATION_TICKS * 16); // Convert ticks to ms
    }
  }
  
  // User actions
  selectPiece(pieceId: number | null): void {
    if (this.state.animating) return;
    
    if (pieceId === this.state.selected) {
      this.removePiece(pieceId);
    } else {
      this.state.selected = pieceId;
      this.dispatchEvent(new CustomEvent(GAME_EVENTS.PIECE_SELECTED, { 
        detail: { pieceId } 
      }));
    }
  }
  
  movePiece(direction: 'left' | 'right'): void {
    if (!this.state.selected || this.state.animating) return;
    
    const piece = this.state.pieces.get(this.state.selected);
    if (!piece) return;
    
    const deltaX = direction === 'left' ? -1 : 1;
    const newCells = piece.cells.map(cell => ({
      x: cell.x + deltaX,
      y: cell.y
    }));
    
    // Check if move is valid
    const isValidMove = newCells.every(cell => {
      if (cell.x < 0 || cell.x >= this.config.cols || 
          cell.y < 0 || cell.y >= this.config.rows) {
        return false;
      }
      const currentOccupant = this.state.board[cell.y][cell.x];
      return currentOccupant === null || currentOccupant === this.state.selected;
    });
    
    if (isValidMove) {
      // Clear old position
      piece.cells.forEach(cell => {
        this.state.board[cell.y][cell.x] = null;
      });
      
      // Update position
      piece.cells = newCells;
      
      // Set new position
      newCells.forEach(cell => {
        this.state.board[cell.y][cell.x] = this.state.selected!;
      });
      
      // Check if piece should start falling
      if (this.canPieceFall(piece)) {
        this.state.fallingPieces.add(this.state.selected);
      }
      
      this.dispatchEvent(new CustomEvent('pieceMoved', {
        detail: { pieceId: this.state.selected, direction }
      }));
    }
  }
  
  removePiece(pieceId: number | null): void {
    if (!pieceId || this.state.animating) return;
    
    const piece = this.state.pieces.get(pieceId);
    if (!piece) return;
    
    // Calculate score
    const baseScore = piece.cells.length * 10;
    const comboMultiplier = 1 + (this.state.combo * 0.5);
    const scoreGain = Math.floor(baseScore * comboMultiplier);
    
    this.state.score += scoreGain;
    this.state.combo++;
    this.state.moves++;
    
    // Clear from board
    piece.cells.forEach(cell => {
      this.state.board[cell.y][cell.x] = null;
    });
    
    // Remove piece
    this.state.pieces.delete(pieceId);
    this.state.selected = null;
    this.state.fallingPieces.delete(pieceId);
    
    // Push back corruption
    this.state.corruptionLevel = Math.max(0, this.state.corruptionLevel - 0.5);
    
    this.dispatchEvent(new CustomEvent(GAME_EVENTS.PIECE_REMOVED, { 
      detail: { pieceId, moves: this.state.moves, score: scoreGain } 
    }));
    
    this.dispatchEvent(new CustomEvent(GAME_EVENTS.SCORE_UPDATE, {
      detail: { score: this.state.score, scoreGain }
    }));
    
    // Check for falling pieces
    this.identifyFallingPieces();
    
    // Check if level is complete
    this.checkLevelComplete();
  }
  
  private clearPathsAndLines(paths: Array<Array<{x: number, y: number}>>, fullLines: number[]): void {
    const cellsToRemove = new Set<string>();
    const piecesToRemove = new Set<number>();
    
    // Collect cells to remove
    paths.forEach(path => {
      path.forEach(cell => {
        cellsToRemove.add(`${cell.x},${cell.y}`);
        const pieceId = this.state.board[cell.y][cell.x];
        if (pieceId !== null) piecesToRemove.add(pieceId);
      });
    });
    
    fullLines.forEach(y => {
      for (let x = 0; x < this.config.cols; x++) {
        cellsToRemove.add(`${x},${y}`);
        const pieceId = this.state.board[y][x];
        if (pieceId !== null) piecesToRemove.add(pieceId);
      }
    });
    
    // Clear cells
    cellsToRemove.forEach(key => {
      const [x, y] = key.split(',').map(Number);
      this.state.board[y][x] = null;
    });
    
    // Update pieces
    piecesToRemove.forEach(pieceId => {
      const piece = this.state.pieces.get(pieceId);
      if (piece) {
        piece.cells = piece.cells.filter(cell => 
          !cellsToRemove.has(`${cell.x},${cell.y}`)
        );
        if (piece.cells.length === 0) {
          this.state.pieces.delete(pieceId);
          this.state.fallingPieces.delete(pieceId);
        }
      }
    });
    
    // Score
    const scoreGain = cellsToRemove.size * 10 * (1 + this.state.combo * 0.5);
    this.state.score += scoreGain;
    this.state.combo++;
    
    this.dispatchEvent(new CustomEvent(GAME_EVENTS.LINE_CLEARED, {
      detail: { 
        paths, 
        fullLines, 
        pathCount: paths.length + fullLines.length,
        totalCells: cellsToRemove.size, 
        bonus: scoreGain 
      }
    }));
  }
  
  private findLeftToRightPaths(): Array<Array<{x: number, y: number}>> {
    const paths: Array<Array<{x: number, y: number}>> = [];
    const visited = new Set<string>();
    
    // Find all possible left-to-right paths
    for (let y = 0; y < this.config.rows; y++) {
      const pieceId = this.state.board[y][0];
      if (pieceId !== null && !visited.has(`0,${y}`)) {
        const path = this.tracePath(0, y, visited);
        if (path.length >= this.config.cols) {
          paths.push(path);
        }
      }
    }
    
    return paths;
  }
  
  private tracePath(startX: number, startY: number, visited: Set<string>): Array<{x: number, y: number}> {
    const path: Array<{x: number, y: number}> = [];
    const queue: Array<{x: number, y: number}> = [{x: startX, y: startY}];
    const pieceId = this.state.board[startY][startX];
    
    while (queue.length > 0) {
      const current = queue.shift()!;
      const key = `${current.x},${current.y}`;
      
      if (visited.has(key)) continue;
      visited.add(key);
      
      if (this.state.board[current.y][current.x] === pieceId) {
        path.push(current);
        
        // Check right neighbor
        if (current.x + 1 < this.config.cols) {
          queue.push({x: current.x + 1, y: current.y});
        }
        
        // Check vertical neighbors for branching paths
        if (current.y > 0) {
          queue.push({x: current.x, y: current.y - 1});
        }
        if (current.y + 1 < this.config.rows) {
          queue.push({x: current.x, y: current.y + 1});
        }
      }
    }
    
    return path;
  }
  
  private findFullLines(): number[] {
    const fullLines: number[] = [];
    for (let y = 0; y < this.config.rows; y++) {
      if (this.state.board[y].every(cell => cell !== null)) {
        fullLines.push(y);
      }
    }
    return fullLines;
  }
  
  private handleGameOver(): void {
    this.state.gameOver = true;
    this.dispatchEvent(new CustomEvent(GAME_EVENTS.GAME_OVER, {
      detail: { score: this.state.score }
    }));
  }
  
  private handleLevelComplete(): void {
    this.dispatchEvent(new CustomEvent(GAME_EVENTS.LEVEL_COMPLETE, { 
      detail: { level: this.state.level, moves: this.state.moves } 
    }));
    
    setTimeout(() => {
      this.state.level++;
      this.generateLevel();
    }, 1500);
  }
  
  generateLevel(): void {
    // Reset for new level
    const currentLevel = this.state.level || 1;
    this.state = this.createInitialState();
    this.state.level = currentLevel;
    this.currentTick = 0;
    
    // Generate pieces (simplified)
    let nextId = 1;
    for (let i = 0; i < 5 + currentLevel; i++) {
      const shape = SHAPES[Math.floor(Math.random() * SHAPES.length)];
      const x = Math.floor(Math.random() * (this.config.cols - 4));
      const y = 0; // Start at top!
      
      const piece = new Piece(nextId, shape, COLORS[i % COLORS.length], x, y);
      this.state.pieces.set(nextId, piece);
      this.state.fallingPieces.add(nextId); // All pieces start falling!
      
      // Don't place on board yet - let them fall
      nextId++;
    }
  }
  
  reset(): void {
    this.state = this.createInitialState();
    this.currentTick = 0;
    this.generateLevel();
  }
  
  setLevel(level: number): void {
    this.state.level = level;
  }
  
  startNewGame(): void {
    this.reset();
  }
  
  // Compatibility method - just calls tick
  update(_deltaTime: number): void {
    // In tick-based system, we ignore deltaTime
    // tick() is already being called at proper rate
  }
  
  // Check if the board is empty (level complete)
  private checkLevelComplete(): void {
    if (this.state.pieces.size === 0 && !this.state.animating) {
      this.handleLevelComplete();
    }
  }
}